'use client';

import Image from 'next/image';

import Skeleton from '@/app/(football)/[team]/_components/common/Skeleton';
import { cn } from '@/app/lib/utils/tailwindcss';

const DEFAULT_PROFILE_IMAGE = '/images/@/@default.svg';

const getRankingImage = (teamSlug: string, ranking: number) => {
  const images: Record<number, { src: string; alt: string }> = {
    1: {
      src: `https://cdn.kottonseed.io/assets/football/2026/${teamSlug}/icons/icon_ranking_gold.webp`,
      alt: '금메달 아이콘',
    },
    2: {
      src: `https://cdn.kottonseed.io/assets/football/2026/${teamSlug}/icons/icon_ranking_silver.webp`,
      alt: '은메달 아이콘',
    },
    3: {
      src: `https://cdn.kottonseed.io/assets/football/2026/${teamSlug}/icons/icon_ranking_bronze.webp`,
      alt: '동메달 아이콘',
    },
  };
  return images[ranking];
};

export interface RankingListItemProps {
  teamSlug: string;
  isMine?: boolean;
  ranking: number;
  profileImage?: string | null;
  nickName: string;
  value: number;
  showMedals?: boolean;
}

export function RankingListItem({
  teamSlug,
  isMine = false,
  ranking,
  profileImage,
  nickName,
  value,
  showMedals = true,
}: RankingListItemProps) {
  const isOverMax = ranking > 999;
  const showMedalImage = showMedals && ranking > 0 && ranking <= 3;
  const rankingImage = showMedalImage ? getRankingImage(teamSlug, ranking) : null;

  return (
    <li
      className={cn(
        'relative flex w-full items-center gap-2 rounded py-3 pr-4 pl-2',
        'shadow-[0px_0px_1.938px_0px_rgba(0,21,45,0.1)] gradient-border gradient-border-ranking-list',
        isMine ? 'bg-ranking-list-my-bg' : 'bg-ranking-list-yours-bg',
      )}
    >
      {/* 랭킹 + 프로필 */}
      <div className="flex w-full items-center gap-1 self-stretch">
        {/* 랭킹 */}
        <div className={cn('flex h-9 w-9 shrink-0 items-center gap-0', isOverMax ? 'justify-start' : 'justify-center')}>
          {ranking === 0 && value === 0 && (
            <p className="text-center text-sm leading-[18px] font-bold text-ranking-list-text">-</p>
          )}

          {showMedalImage && rankingImage && (
            <div className="flex h-7 w-7 items-center justify-center overflow-hidden rounded-full">
              <Image src={rankingImage.src} alt={rankingImage.alt} width={28} height={28} />
            </div>
          )}

          {!showMedalImage && ranking > 0 && ranking <= 999 && (
            <p className="text-center text-sm leading-[18px] font-bold text-ranking-list-text">{ranking}</p>
          )}

          {isOverMax && (
            <>
              <p className="text-center text-sm leading-[18px] font-bold text-ranking-list-text">999</p>
              <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
                <g clipPath="url(#clip0_1149_738)">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M4.6 3.2V0H3.4V3.2H0V4.4H3.4V8H4.6V4.4H8V3.2H4.6Z"
                    fill="white"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_1149_738">
                    <rect width="8" height="8" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </>
          )}
        </div>

        {/* 프로필 */}
        <div className="flex w-full items-center gap-2">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#630209]">
            <Image src={profileImage ?? DEFAULT_PROFILE_IMAGE} alt={`${nickName} 프로필 이미지`} width={36} height={36} />
          </div>
          <p className="line-clamp-1 w-full text-sm leading-[18px] font-bold break-all text-ranking-list-text">
            {nickName}
          </p>
        </div>
      </div>

      {/* 값 */}
      <p className="w-[35px] text-right text-sm leading-[18px] font-bold text-ranking-list-text">{value}</p>
    </li>
  );
}

export function RankingListItemSkeleton() {
  return (
    <li className="relative flex w-full items-center gap-2 rounded py-3 pr-4 pl-2 gradient-border gradient-border-ranking-list bg-ranking-list-yours-bg">
      <div className="flex w-full items-center gap-1 self-stretch">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center">
          <Skeleton width={28} height={28} />
        </div>
        <div className="flex w-full items-center gap-2">
          <Skeleton width={36} height={36} borderRadius={99} className="shrink-0" />
          <Skeleton width="100%" height={18} />
        </div>
      </div>
      <Skeleton width={35} height={18} />
    </li>
  );
}

export interface RankingListEmptyProps {
  imageSrc: string;
  imageAlt: string;
  children: React.ReactNode;
}

export function RankingListEmpty({ imageSrc, imageAlt, children }: RankingListEmptyProps) {
  return (
    <li className="relative flex w-full flex-col items-center justify-center gap-4 rounded bg-white/8 px-4 py-6 gradient-border gradient-border-ranking-list-empty">
      <Image src={imageSrc} alt={imageAlt} width={48} height={48} />
      <p className="w-full text-center typo-body-6 text-g-00">{children}</p>
    </li>
  );
}
